const LOCAL_API_ORIGIN = "http://localhost:8000";
const PRODUCTION_API_ORIGIN = "https://southcentraldev.com";

const DEFAULT_API_ORIGIN =
  typeof window !== "undefined" && ["localhost", "127.0.0.1"].includes(window.location.hostname)
    ? LOCAL_API_ORIGIN
    : PRODUCTION_API_ORIGIN;

const API_ORIGIN = (
  process.env.REACT_APP_API_ORIGIN ||
  PRODUCTION_API_ORIGIN 
).replace(/\/+$/, "");

export const API_URL =
  `${API_ORIGIN}/api/sugalumps`;

export const BILLING_API_URL =
  `${API_ORIGIN}/api/billing`;

export const MAIN_API_URL =
  `${API_ORIGIN}/api/admin`;

export const EMAIL_API_URL =
  `${API_ORIGIN}/api/email`;

export const AUTH_URL =
  `${API_ORIGIN}/api/admin/token/`;

export const REFRESH_URL =
  `${API_ORIGIN}/api/admin/token/refresh/`;

export const SITE = "suga-lump";
export const ADMIN_LOGIN_PATH = "/login";

const ACCESS_TOKEN_KEY = "sugalump_access_token";
const REFRESH_TOKEN_KEY = "sugalump_refresh_token";
const USER_KEY = "sugalump_user";

let refreshPromise = null;

export function getAccessToken() {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);

  if (!token || token === "null" || token === "undefined") {
    return "";
  }

  return token;
}

export function getRefreshToken() {
  const token = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!token || token === "null" || token === "undefined") return "";
  return token;
}

export function getStoredUser() {
  const storedUser = localStorage.getItem(USER_KEY);

  if (!storedUser) {
    return null;
  }

  try {
    return JSON.parse(storedUser);
  } catch {
    return null;
  }
}

export function saveTokens({ access, refresh }) {
  if (access) localStorage.setItem(ACCESS_TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
}

export function saveAuthSession(data) {
  if (!data?.access || !data?.refresh) {
    throw new Error("The login response did not contain valid tokens.");
  }

  saveTokens({ access: data.access, refresh: data.refresh });

  localStorage.setItem(
    USER_KEY,
    JSON.stringify({
      username: data.username,
      displayName: data.display_name || data.username,
      site: data.site,
      actingSite: data.acting_site,
      role: data.role || "staff",
      isSuperuser: Boolean(data.is_superuser),
      sugalumpAccess: Boolean(data.sugalump_access),
    }),
  );
}

export function clearAuthSession() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);

  // Remove old token keys from earlier development.
  localStorage.removeItem("access");
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh");
  localStorage.removeItem("refresh_token");
}

function routeToLogin() {
  if (typeof window === "undefined") return;
  if (window.location.pathname !== ADMIN_LOGIN_PATH) {
    window.location.assign(ADMIN_LOGIN_PATH);
  }
}

export function clearAuthAndRedirect() {
  clearAuthSession();
  routeToLogin();
}

export function isAuthenticated() {
  return Boolean(getAccessToken());
}

export function getAuthHeaders() {
  const token = getAccessToken();

  if (!token) {
    return {};
  }

  return {
    Authorization: `Bearer ${token}`,
  };
}

async function refreshAccessToken() {
  const refresh = getRefreshToken();
  if (!refresh) throw new Error("Missing refresh token.");

  const response = await fetch(REFRESH_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({ refresh }),
  });

  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json") ? await response.json() : {};

  if (!response.ok || !data?.access) {
    throw new Error(data?.detail || "Session refresh failed.");
  }

  saveTokens({ access: data.access, refresh: data.refresh });
  return data.access;
}

function isAuthEndpoint(input) {
  const url = typeof input === "string" ? input : input?.url || "";
  return url.startsWith(AUTH_URL) || url.startsWith(REFRESH_URL);
}

function withAuthHeader(init = {}, accessToken = getAccessToken()) {
  const headers = new Headers(init.headers || {});
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
  return { ...init, headers };
}

export async function authFetch(input, init = {}, options = {}) {
  const skipRefresh = options.skipRefresh || isAuthEndpoint(input);
  let response = await fetch(input, withAuthHeader(init));

  if (response.status !== 401 || skipRefresh || options._retried) {
    return response;
  }

  try {
    if (!refreshPromise) {
      refreshPromise = refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }
    const access = await refreshPromise;
    response = await fetch(input, withAuthHeader(init, access));
    if (response.status === 401) throw new Error("Retried request was unauthorized.");
    return response;
  } catch (error) {
    if (process.env.NODE_ENV === "development") console.error("Admin session refresh failed", error);
    clearAuthAndRedirect();
    return response;
  }
}

async function parseResponse(response) {
  const contentType =
    response.headers.get("content-type") || "";

  let data;

  if (contentType.includes("application/json")) {
    data = await response.json();
  } else {
    await response.text();
    data = {
      detail: "The request could not be completed.",
    };
  }

  if (!response.ok) {
    const error = new Error(
      data?.detail ||
        data?.message ||
        "The request could not be completed.",
    );

    error.status = response.status;
    error.data = data;

    throw error;
  }

  return data;
}

export async function loginUser({ username, password }) {
  const response = await fetch(AUTH_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      username: username.trim(),
      password,
      site: SITE,
    }),
  });

  const data = await parseResponse(response);

  if (
    !data.is_superuser &&
    data.acting_site !== SITE &&
    data.site !== SITE
  ) {
    throw new Error(
      "This account does not have access to SugarLumps.",
    );
  }

  if (
    data.sugalump_access === false &&
    !data.is_superuser
  ) {
    throw new Error(
      "This account does not have access to SugarLumps.",
    );
  }

  saveAuthSession(data);

  return data;
}

export function logoutUser() {
  clearAuthSession();
}

export async function resetPassword({ uid, token, password }) {
  const response = await fetch(`${EMAIL_API_URL}/reset-password-confirm/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      uid,
      token,
      password,
    }),
  });

  return parseResponse(response);
}